import { Body2 } from '@web/components/atoms/Typography'
import { useEffect, useMemo, useState } from 'react'
import { useMergeFields } from '../Text/MergeTags/MergeTag'
import { BonusGoal } from '@web/graphql-types'
import { MATCHED_GIVING_MERGE_TAGS, PROGRESS_BAR_MERGE_TAGS } from '../Text/MergeTags/utils'
import ProgressBar from '@web/components/molecules/ProgressBar'
import { formatAmount, toDisplayAmountNoDecimal } from '@web/components/utils/format'
import { ProgressBarType } from './progressBarUtils'

type ProgressDisplayProps = {
  type?: ProgressBarType
  amountRaised: number
  goals: number[]
  barColor: string
  progressColor: string
  bonusGoals?: BonusGoal[]
  finalGoal?: BonusGoal
  isAnimated?: boolean
  isLooping?: boolean
  switchGoal: (index: number) => void
  progressBarIcon?: string
}

const INTERVAL_AMOUNT = 20
const [, PERCENTAGE_TAG, AMOUNT_RAISED_TAG] = PROGRESS_BAR_MERGE_TAGS
const [, MG_PERCENTAGE_TAG, MG_AMOUNT_RAISED_TAG] = MATCHED_GIVING_MERGE_TAGS

const ProgressDisplay = ({
  type,
  amountRaised,
  goals,
  barColor,
  progressColor,
  switchGoal,
  bonusGoals,
  isLooping,
  progressBarIcon,
  isAnimated = true,
}: ProgressDisplayProps) => {
  const setMergeField = useMergeFields((state) => state.setMergeField)
  const [prevAmount, setPrevAmount] = useState(0)
  const [progress, setProgress] = useState(0)
  const totalAmount = useMemo(() => {
    return goals.reduce((acc, goal) => {
      if (amountRaised < acc) {
        return acc
      }
      return (acc += goal)
    }, 0)
  }, [goals, amountRaised])
  const [currentGoal, pastGoals, goalIndex] = useMemo(() => {
    let sum = 0
    const breakpoints: number[] = []
    for (let index = 0; index < goals.length; index++) {
      const goal = goals[index]
      sum += goal
      if (!bonusGoals?.[index]?.isTickHidden) {
        breakpoints.push(sum)
      }
      if (progress < sum) {
        return [sum, breakpoints.slice(0, index), index]
      }
    }

    return [goals.reduce((acc, goal) => (acc += goal), 0), breakpoints, goals.length - 1]
  }, [goals, progress])
  const progressPercentage = useMemo(() => {
    return (progress / (totalAmount || currentGoal || 1)) * 100
  }, [progress, currentGoal])

  useEffect(() => {
    switchGoal(goalIndex)
  }, [goalIndex])

  useEffect(() => {
    const percentage = `${Math.floor(isNaN(progressPercentage) ? 0 : progressPercentage)}`
    const progressString = `${toDisplayAmountNoDecimal(progress)}`

    if (type === ProgressBarType.MatchedGiving) {
      setMergeField(MG_PERCENTAGE_TAG.MergeFieldSettingId, percentage)
      setMergeField(MG_AMOUNT_RAISED_TAG.MergeFieldSettingId, progressString)

      return
    }

    setMergeField('progress_bar.percentage_of_goal_raised', percentage)
    setMergeField(PERCENTAGE_TAG.MergeFieldSettingId, percentage)
    setMergeField(AMOUNT_RAISED_TAG.MergeFieldSettingId, progressString)
  }, [progressPercentage])

  useEffect(() => {
    let interval: NodeJS.Timeout
    let restartTimeout: NodeJS.Timeout
    let loopTimeout: NodeJS.Timeout
    const animateProgress = (amount: number) => {
      if (!isAnimated) {
        setProgress(amountRaised)

        if (interval) {
          clearInterval(interval)
        }
        return
      }

      if (amountRaised === 0) {
        return
      }

      const intervalAmount = amount / INTERVAL_AMOUNT
      let i = 0
      interval = setInterval(() => {
        setProgress((prev) => Math.floor(prev + intervalAmount))
        i++

        if (i >= INTERVAL_AMOUNT) {
          clearInterval(interval!)

          if (isLooping && isAnimated) {
            restartTimeout = setTimeout(() => {
              setProgress(0)
              loopTimeout = setTimeout(() => {
                animateProgress(amountRaised)
              }, 500)
            }, 1000)
          } else {
            setProgress(amountRaised)
          }
        }
      }, 200)
    }

    setProgress(prevAmount)
    animateProgress(amountRaised - prevAmount)
    setPrevAmount(amountRaised)
    return () => {
      clearInterval(interval)
      clearTimeout(restartTimeout)
      clearTimeout(loopTimeout)
    }
  }, [amountRaised, isAnimated])

  return (
    <ProgressBar
      progressPercentage={Math.floor(progressPercentage)}
      progressColor={progressColor}
      goals={goals}
      bonusGoals={bonusGoals?.map((goal) => ({ customTickColor: goal.customTickColor ?? undefined }))}
      totalAmount={totalAmount ?? currentGoal}
      isAnimated={isAnimated}
      barColor={barColor}
      pastGoals={pastGoals}
      progressBarIcon={progressBarIcon}
      footerSlot={
        <>
          <Body2 as="strong" fontWeight={600}>
            ${formatAmount((progress === amountRaised ? progress : Math.floor(progress)) ?? 0)} raised
          </Body2>
          <Body2 as="strong" fontWeight={600}>
            ${formatAmount(totalAmount ?? currentGoal)} goal
          </Body2>
        </>
      }
    />
  )
}

export default ProgressDisplay
